import { inject, Injectable } from '@angular/core';
import { ConfigService } from '../config.service';
import { QuoteInfo, SaleApprovalChecklistItems } from '../sdk/sdk';

/**
 * Service that handles business logic for pending sales action/overview components.
 * Provides state computation, sale detection, and external navigation functionality.
 */
@Injectable({
  providedIn: 'root',
})
export class PendingSalesStateService {
  private readonly configService = inject(ConfigService);

  /**
   * Computes the current state based on loading status, errors, and data availability.
   *
   * @param isLoading - Whether data is currently being loaded
   * @param hasError - Whether an error occurred during data loading
   * @param appointments - List of appointments
   * @param quote - Quote information
   * @returns The computed state: 'loading', 'error', 'no-matches', or 'matches'
   */
  computeState(
    isLoading: boolean,
    hasError: boolean,
    appointments: unknown[],
    quote: QuoteInfo | null
  ): 'loading' | 'error' | 'no-matches' | 'matches' {
    if (isLoading) {
      return 'loading';
    }
    if (hasError) {
      return 'error';
    }
    const hasAppointments = appointments && appointments.length > 0;
    if (!hasAppointments && !quote) {
      return 'no-matches';
    }
    return 'matches';
  }

  /**
   * Determines if a sale exists for the given quote.
   *
   * @param quote - Quote information
   * @returns True if the quote has an associated sale, false otherwise
   */
  hasSale(quote: QuoteInfo | null): boolean {
    return quote?.sale !== null && quote?.sale !== undefined && !quote?.sale?.isCancelled;
  }

  /**
   * Gets the completed sale approval items from a quote.
   *
   * @param quote - Quote information
   * @returns The completed sale approval items, or None if quote is null
   */
  getCompletedItems(quote: QuoteInfo | null): SaleApprovalChecklistItems {
    const value = quote?.completedSaleApprovalItems;
    return value ?? SaleApprovalChecklistItems.None;
  }

  getSystemVerifiedItems(quote: QuoteInfo | null): SaleApprovalChecklistItems {
    return quote?.systemVerifiedSaleApprovalItems ?? SaleApprovalChecklistItems.None;
  }

  /**
   * Opens the call center application to schedule a revisit for the given opportunity.
   * Opens in a new browser tab.
   *
   * @param opportunityId - The opportunity ID to schedule a revisit for
   */
  openScheduleRevisit(opportunityId: string | null): void {
    if (!opportunityId) {
      return;
    }

    const callCenterAppUrl = this.configService.callCenterAppUrl;
    const url = `${callCenterAppUrl}/opportunity?opportunityId=${opportunityId}`;

    window.open(url, '_blank');
  }
}
